// НОВОЕ: Добавляем импорты иконок и нужных модалок
import React, { useState } from "react";
import { ArrowRight, ArrowLeft, RefreshCw, Plus, Check, X, Building, Trash2, Edit3, Wallet, Tags, User, Archive, Upload, FileText, AlertCircle, Loader } from "lucide-react";
import { useTheme, uid, fmt, fmtDate, Card, Btn, PageHeader, NumInput, DateField, HelpTooltip, addAuditLog, SearchableSelect, getLocale, AccountOptionsGrouped, confirmHiddenAccountChoice } from "./shared";
import { HELP_TEXTS } from "./HelpText";
import { parseInvoice, matchInvoiceToOrg } from "./invoiceParser";
import { S } from './strings';
import * as api from './api';
// Убедись, что эти компоненты экспортируются из своих файлов!
// import ContractorModal from "./ContractorsTab"; // Если он вынесен
// import { TxEditModal } from "./DataTab";

// === ПЕРЕРАБОТАННОЕ: Полноценное окно просмотра/оплаты долга (вынесено из DebtsTab) ===
function ViewDebtModal({ viewDebt, setViewDebt, data, t, isCompact, perms, user, uOrg, apiSync, delDebt, orgId }) {
    const isRec = viewDebt.type === 'receivable';
    const color = isRec ? t.accentGreen : t.accentRed;
    const con = (data.contractors || []).find(c => c.id === viewDebt.contractorId);

    const acc = (data.accounts || []).find(a => a.id === viewDebt.accountId);
    const fund = (data.funds || []).find(f => f.id === viewDebt.fundId);
    const cat = isRec
        ? (data.incomeCategories || []).find(c => c.id === viewDebt.categoryId)
        : (data.expenseCategories || []).find(c => c.id === viewDebt.categoryId);

    // Расчёт сумм
    const paidAmount = viewDebt.paidAmount || 0;
    const remaining = viewDebt.amount - paidAmount;
    const payments = viewDebt.payments || [];
    const paidPercent = viewDebt.amount > 0 ? Math.min(100, Math.round((paidAmount / viewDebt.amount) * 100)) : 0;

    // Стейты модалки
    const [payMode, setPayMode] = useState(null); // null | 'full' | 'partial'
    const [partialAmt, setPartialAmt] = useState('');
    const [payComment, setPayComment] = useState(viewDebt.description || ''); // по умолчанию = «за что платим» долга
    const [payAccId, setPayAccId] = useState(viewDebt.accountId || (data.accounts || []).find(a => !a.archived)?.id || '');
    const [payFundId, setPayFundId] = useState(viewDebt.fundId || (data.funds || []).find(f => !f.archived)?.id || '');
    const [payCatId, setPayCatId] = useState(viewDebt.categoryId || '');
    const [isEditing, setIsEditing] = useState(false);
    // P1.3 (2026-05-21): submit-lock для оплаты долга. Двойной клик = двойное списание
    // со счёта и две Operation. useRef — синхронный guard (мгновенный, не зависит от
    // React rerender), paying state — для disabled на кнопке (визуальная обратная связь).
    const submittingPayRef = React.useRef(false);
    const [paying, setPaying] = useState(false);
    const [editData, setEditData] = useState({
      description: viewDebt.description || '',
      date: viewDebt.date || '',
      contractorId: viewDebt.contractorId || '',
      accountId: viewDebt.accountId || '',
      fundId: viewDebt.fundId || '',
      categoryId: viewDebt.categoryId || '',
    });

    const aAccs = (data.accounts || []).filter(a => !a.archived && !a.isHidden);
    const aFunds = (data.funds || []).filter(f => !f.archived);
    const availableCats = isRec ? (data.incomeCategories || []) : (data.expenseCategories || []);

    // === ОПЛАТА (полная/частичная) ===
    // === ОПЛАТА (полная/частичная) — O4 (2026-06-10): SEQUENTIAL ===
    // Сначала POST /operations (backend атомарно: операция + балансы/фонды + долг в одной $tx),
    // и только при успехе обновляем модалку + мягко перезагружаем орг. Фантома при сбое нет.
    const doPayment = async (payAmount) => {
      if (submittingPayRef.current) return;
      if (!payAmount || payAmount <= 0) return;
      if (payAmount > remaining + 0.01) { alert(S().debts.paymentExceedsRemaining(fmt(payAmount), fmt(remaining))); return; }
      if (!payCatId && availableCats.length > 0) {
        if (!window.confirm(S().debts.noCategoryConfirm)) return;
      }
      if (!apiSync) { alert((S().common && S().common.unknownError) || 'Нет связи с сервером'); return; }

      submittingPayRef.current = true;
      setPaying(true);

      const debtAmount = viewDebt.amount;
      const isFull = (paidAmount + payAmount) >= debtAmount;
      const partialSuffix = !isFull ? S().debts.debtPaymentPartial(fmt(paidAmount + payAmount), fmt(debtAmount)) : '';
      const commentText = `${S().debts.debtPaymentComment((payComment && payComment.trim()) || viewDebt.description || con?.name || '')}${partialSuffix}`;
      const conObj = (data.contractors || []).find(c => c.id === viewDebt.contractorId);

      let builtTx = null;
      if (isRec) {
        const activeFunds = (data.funds || []).filter(f => !f.archived);
        const bd = [];
        activeFunds.forEach((f, idx) => {
          let pt = Math.round(payAmount * f.percent) / 100;
          if (idx === activeFunds.length - 1) {
            const allocated = bd.reduce((s, b) => s + b.amount, 0);
            pt = Math.round((payAmount - allocated) * 100) / 100;
          }
          bd.push({ fundId: f.id, fundName: f.name, amount: pt });
        });
        const incCat = (data.incomeCategories || []).find(c => c.id === (payCatId || availableCats[0]?.id));
        builtTx = {
          id: uid(), type: 'income', accountId: payAccId, amount: payAmount,
          comment: commentText,
          breakdown: bd, userId: user?.id, userName: user?.name, date: new Date().toISOString(),
          incomeCategoryId: incCat?.id || null, incomeCategoryName: incCat?.name || '', debtId: viewDebt.id,
          contractorId: viewDebt.contractorId || '', contractorName: conObj?.name || con?.name || '',
        };
      } else {
        const expCat = (data.expenseCategories || []).find(c => c.id === (payCatId || availableCats[0]?.id));
        const fundFromData = (data.funds || []).find(f => f.id === payFundId);
        builtTx = {
          id: uid(), type: 'expense', accountId: payAccId, fundId: payFundId,
          fundName: fundFromData?.name || aFunds.find(f => f.id === payFundId)?.name || '', amount: payAmount,
          comment: commentText,
          expenseCategoryId: expCat?.id || null, expenseCategoryName: expCat?.name || '',
          userId: user?.id, userName: user?.name, date: new Date().toISOString(), debtId: viewDebt.id,
          contractorId: viewDebt.contractorId || '', contractorName: conObj?.name || con?.name || '',
        };
      }
      builtTx.debtPayment = { paymentRecord: { userName: user?.name || 'system' } };

      let result = null;
      try { result = await apiSync.syncCreateOperation(builtTx); } catch (e) { result = null; }
      if (!result) { submittingPayRef.current = false; setPaying(false); return; }

      addAuditLog(uOrg, user, {
        type: isRec ? 'income_debt' : 'expense_debt',
        desc: `Оплата по обязательству: ${isRec ? '+' : '-'}${fmt(payAmount)}`,
        details: `Долг: ${viewDebt.description}. Остаток после: ${fmt(remaining - payAmount)}. ${(data.accounts||[]).find(a=>a.id===payAccId)?.name}`
      });

      if (isFull) {
        setViewDebt(null);
      } else {
        setViewDebt({
          ...viewDebt,
          paidAmount: paidAmount + payAmount,
          payments: [...payments, { id: uid(), amount: payAmount, date: new Date().toISOString(), userName: user?.name || S().debts.systemUser }],
          status: 'partial',
        });
        setPayMode(null);
        setPartialAmt('');
        setPayComment('');
        submittingPayRef.current = false;
        setPaying(false);
      }
      try { window.dispatchEvent(new CustomEvent('inmonex-reload-org')); } catch (e) {}
    };

    // O-debt (2026-06-10): sequential — await PUT, при успехе reload-org.
    const saveEdit = async () => {
      if (!apiSync) { setIsEditing(false); return; }
      const activeCats = isRec ? (data.incomeCategories||[]) : (data.expenseCategories||[]);
      const fallbackCatId = activeCats.length > 0 ? activeCats[0].id : '';
      const payload = { ...editData, categoryId: editData.categoryId || fallbackCatId };
      const r = await apiSync.syncUpdateDebt(viewDebt.id, payload);
      if (!r) return;
      setViewDebt({ ...viewDebt, ...payload });
      addAuditLog(uOrg, user, {
        type: 'debt_edit',
        desc: `Изменение обязательства: ${editData.description}`,
        details: `Срок: ${fmtDate(editData.date)}. Контрагент: ${(data.contractors||[]).find(c=>c.id===editData.contractorId)?.name}`
      });
      setIsEditing(false);
      try { window.dispatchEvent(new CustomEvent('inmonex-reload-org')); } catch (e) {}
    };

    // O-debt (2026-06-10): sequential — await PUT, при успехе reload-org.
    const archiveDebt = async () => {
      if (!window.confirm(S().debts.archiveConfirm)) return;
      if (!apiSync) { setViewDebt(null); return; }
      const r = await apiSync.syncUpdateDebt(viewDebt.id, { status: 'archived' });
      if (!r) return;
      addAuditLog(uOrg, user, {
        type: 'debt_archive',
        desc: `Архивация обязательства: ${viewDebt.description}`,
        details: `Контрагент: ${con?.name}. Остаток при архивации: ${fmt(remaining)}`
      });
      setViewDebt(null);
      try { window.dispatchEvent(new CustomEvent('inmonex-reload-org')); } catch (e) {}
    };

    // === RENDER: РЕЖИМ РЕДАКТИРОВАНИЯ ===
    if (isEditing) {
      const editContractors = (data.contractors || []).filter(c => isRec ? c.isCustomer !== false : c.isSupplier !== false);
      return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => { if (e.target === e.currentTarget) (() => setIsEditing(false))(); }}>
          <div onClick={e => e.stopPropagation()} style={{ background: t.bgCard, border: `2px solid ${t.accent}`, borderRadius: 16, padding: 24, width: 480, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,.4)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>✏️ {S().debts.editDebtTitle}</div>
              <button onClick={() => setIsEditing(false)} style={{ background:'#fee2e2', border:'2px solid #b91c1c', borderRadius:10, padding:5, color:'#b91c1c', cursor:'pointer' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'grid', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>{S().debts.editContractorLabel}</label>
                <SearchableSelect
                  value={editData.contractorId}
                  onChange={val => setEditData({ ...editData, contractorId: val })}
                  options={editContractors.map(c => ({value: c.id, label: c.name, subLabel: c.bin}))}
                  placeholder={S().debts.editContractorPlaceholder}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>{S().debts.descriptionLabel}</label>
                <input value={editData.description} onChange={e => setEditData({ ...editData, description: e.target.value })} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}` }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>{S().debts.expectedPaymentDate}</label>
                <DateField value={editData.date} onChange={v => setEditData({ ...editData, date: v })} width="100%" />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                {isRec ? (<>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>{S().debts.editAccountCreditLabel}</label>
                    <select value={editData.accountId} onChange={e => setEditData({ ...editData, accountId: e.target.value })} style={{ width: '100%', padding: 8, borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}` }}>
                      <option value="">{S().common.notSpecified}</option>
                      {aAccs.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>{S().debts.incomeCategoryLabel}</label>
                    <select value={editData.categoryId} onChange={e => setEditData({ ...editData, categoryId: e.target.value })} style={{ width: '100%', padding: 8, borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}` }}>
                      {(data.incomeCategories || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                </>) : (<>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>{S().debts.editFundDebitLabel}</label>
                    <select value={editData.fundId} onChange={e => setEditData({ ...editData, fundId: e.target.value })} style={{ width: '100%', padding: 8, borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}` }}>
                      <option value="">{S().common.notSpecified}</option>
                      {aFunds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>{S().debts.expenseCategoryLabel}</label>
                    <select value={editData.categoryId} onChange={e => setEditData({ ...editData, categoryId: e.target.value })} style={{ width: '100%', padding: 8, borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}` }}>
                      {(data.expenseCategories || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                </>)}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <Btn variant="ghost" onClick={() => setIsEditing(false)} style={{ flex: 1, justifyContent: 'center', padding: 12 }}>{S().common.cancel}</Btn>
              <Btn variant="primary" onClick={saveEdit} style={{ flex: 1, justifyContent: 'center', padding: 12 }}><Check size={16} /> {S().common.save}</Btn>
            </div>
          </div>
        </div>
      );
    }

    // === RENDER: ОСНОВНАЯ МОДАЛКА ===
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onMouseDown={e => { if (e.target === e.currentTarget) (() => setViewDebt(null))(); }}>
        <div onClick={e => e.stopPropagation()} style={{ background: t.bgCard, border: `2px solid ${color}`, borderRadius: 16, padding: isCompact ? 16 : 24, width: 500, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.4)' }}>

          {/* ШАПКА — единый стиль: заголовок слева, иконки справа */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color }}>
              {isRec ? `📥 ${S().debts.viewWeOweUs}` : `📤 ${S().debts.viewWeOwe}`}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <HelpTooltip text={HELP_TEXTS.debtViewInfo} size={15} />
              {perms.manageDebts && (
                <>
                  <button onClick={() => setIsEditing(true)} title={S().debts.editBtnTitle} style={{ background: t.bgInput, borderRadius: 8, padding: 6, color: t.textMuted, border: 'none', cursor: 'pointer', transition: '0.2s' }} onMouseEnter={e => e.currentTarget.style.color = t.accent} onMouseLeave={e => e.currentTarget.style.color = t.textMuted}><Edit3 size={16} /></button>
                  <button onClick={archiveDebt} title={S().debts.archiveBtnTitle} style={{ background: t.bgInput, borderRadius: 8, padding: 6, color: t.textMuted, border: 'none', cursor: 'pointer', transition: '0.2s' }} onMouseEnter={e => e.currentTarget.style.color = t.accentOrange} onMouseLeave={e => e.currentTarget.style.color = t.textMuted}><Archive size={16} /></button>
                  <button onClick={() => { delDebt(viewDebt.id); setViewDebt(null); }} title={S().debts.deleteBtnTitle} style={{ background: t.bgInput, borderRadius: 8, padding: 6, color: t.textMuted, border: 'none', cursor: 'pointer', transition: '0.2s' }} onMouseEnter={e => e.currentTarget.style.color = t.accentRed} onMouseLeave={e => e.currentTarget.style.color = t.textMuted}><Trash2 size={16} /></button>
                </>
              )}
              <button onClick={() => setViewDebt(null)} title={S().debts.closeBtnTitle} style={{ background:'#fee2e2', border:'2px solid #b91c1c', borderRadius:10, padding:5, color:'#b91c1c', cursor:'pointer' }}><X size={18} /></button>
            </div>
          </div>

          {/* ИНФО О КОНТРАГЕНТЕ */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, padding: '12px 14px', background: t.bgInput, borderRadius: 12, border: `1px solid ${t.border}` }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: `${color}15`, color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <User size={20} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{con ? con.name : S().common.unknown}</div>
              <div style={{ fontSize: 12, color: t.textMuted }}>{viewDebt.description || S().common.noDescription}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: t.textMuted }}>{S().debts.dateLabel}</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{viewDebt.date ? new Date(viewDebt.date).toLocaleDateString(getLocale()) : '—'}</div>
            </div>
          </div>

          {/* ФИНАНСОВЫЙ БЛОК */}
          <div style={{ padding: 14, background: `${color}08`, borderRadius: 14, border: `1px solid ${color}25`, marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, textTransform: 'uppercase' }}>{S().debts.totalAmount}</div>
                <div className="mono" style={{ fontSize: 14, fontWeight: 800, color: t.text, whiteSpace: 'nowrap' }}>{fmt(viewDebt.amount)}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, textTransform: 'uppercase' }}>{S().debts.paidLabelUpper}</div>
                <div className="mono" style={{ fontSize: 14, fontWeight: 800, color: t.accentGreen, whiteSpace: 'nowrap' }}>{fmt(paidAmount)}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, textTransform: 'uppercase' }}>{S().debts.remainingLabel}</div>
                <div className="mono" style={{ fontSize: 14, fontWeight: 800, color: remaining > 0 ? color : t.accentGreen, whiteSpace: 'nowrap' }}>{fmt(remaining)}</div>
              </div>
            </div>
            {/* ПРОГРЕСС-БАР */}
            <div style={{ height: 6, background: `${t.textMuted}20`, borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${paidPercent}%`, background: paidPercent >= 100 ? t.accentGreen : color, borderRadius: 3, transition: 'width 0.5s ease' }} />
            </div>
            <div style={{ fontSize: 10, color: t.textMuted, marginTop: 3, textAlign: 'right' }}>{S().debts.paidPercent(paidPercent)}</div>
          </div>



          {/* ИСТОРИЯ ОПЛАТ */}
          {payments.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>📜 {S().debts.paymentHistory(payments.length)}</div>
                <HelpTooltip text={HELP_TEXTS.debtPartial} size={13} />
              </div>
              <div style={{ maxHeight: 120, overflowY: 'auto', borderRadius: 10, border: `1px solid ${t.border}` }}>
                {payments.map((p, i) => (
                  <div key={p.id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: i < payments.length - 1 ? `1px solid ${t.border}` : 'none', background: i % 2 === 0 ? t.bgInput : t.bgCard }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Check size={14} color={t.accentGreen} />
                      <span className="mono" style={{ fontSize: 13, fontWeight: 700, color: t.accentGreen }}>+{fmt(p.amount)}</span>
                    </div>
                    <div style={{ fontSize: 11, color: t.textMuted }}>{p.userName || '—'} · {p.date ? new Date(p.date).toLocaleDateString(getLocale()) : '—'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ВЛОЖЕННЫЙ ФАЙЛ */}
          {viewDebt.attachmentPath && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: t.text, marginBottom: 8 }}>{S().debts.attachmentLabel}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: t.bgInput, borderRadius: 10, border: `1px solid ${t.border}` }}>
                <FileText size={18} color={t.accent}/>
                <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{viewDebt.attachmentName || 'invoice.pdf'}</div>
                <button
                  onClick={() => { api.openDebtAttachment(orgId, viewDebt.id).catch(e => alert(e.message || 'Не удалось открыть файл')); }}
                  style={{ background: t.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', textDecoration: 'none' }}
                >
                  {S().debts.downloadBtn}
                </button>
              </div>
              <div style={{ fontSize: 10, color: t.textMuted, marginTop: 4 }}>
                PDF-файл хранится на сервере 90 дней после оплаты, затем автоматически удаляется.
              </div>
            </div>
          )}

          {/* БЛОК ОПЛАТЫ */}
          {remaining > 0 && perms.manageDebts && (
            <div style={{ padding: 16, background: `${color}06`, borderRadius: 14, border: `2px solid ${color}30` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color }}>{isRec ? '💰' : '💸'} {S().debts.fixPayment}</div>
                <HelpTooltip text={HELP_TEXTS.debtPayment} size={14} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12, alignItems: 'start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3, height: 20 }}>
                    <label style={{ fontSize: 11, color: t.textMuted, fontWeight: 700 }}>💳 {isRec ? S().debts.toWhichAccount : S().debts.fromWhichAccount}</label>
                  </div>
                  <select value={payAccId} onChange={e => setPayAccId(e.target.value)} style={{ width: '100%', padding: 7, borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}`, fontSize: 13, height: 34 }}>
                    {aAccs.map(a => <option key={a.id} value={a.id}>{a.name} ({fmt(data.accountBalances?.[a.id] || 0)})</option>)}
                  </select>
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3, height: 20 }}>
                    <label style={{ fontSize: 11, color: t.textMuted, fontWeight: 700 }}>🏷 {isRec ? S().debts.categoryIncomeLabel : S().debts.categoryExpenseLabel}</label>
                    <HelpTooltip text={S().debts.categoryHelpTooltip} size={12} />
                  </div>
                  <select value={payCatId} onChange={e => setPayCatId(e.target.value)} style={{ width: '100%', padding: 7, borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}`, fontSize: 13, height: 34 }}>
                    {availableCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  {!payCatId && availableCats.length > 0 && <div style={{ fontSize: 10, color: t.accentOrange, marginTop: 2 }}>⚠ {S().debts.recommendSelect}</div>}
                </div>
                {!isRec && (
                  <div style={{ gridColumn: 'span 2' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3, minHeight: 16 }}>
                      <label style={{ fontSize: 11, color: t.textMuted, fontWeight: 700 }}>📊 {S().debts.fromWhichFund}</label>
                    </div>
                    <select value={payFundId} onChange={e => setPayFundId(e.target.value)} style={{ width: '100%', padding: 7, borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}`, fontSize: 13 }}>
                      {aFunds.map(f => <option key={f.id} value={f.id}>{f.name} ({fmt(data.fundBalances?.[f.id] || 0)})</option>)}
                    </select>
                  </div>
                )}
              </div>

              {/* Комментарий к оплате — применяется и к полной, и к частичной */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: t.textMuted, display: 'block', marginBottom: 4 }}>{S().debts.paymentCommentLabel || 'Комментарий к оплате (необязательно)'}</label>
                <input value={payComment} onChange={e => setPayComment(e.target.value)} placeholder={S().debts.paymentCommentPlaceholder || 'Например: за январь'} style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgInput, color: t.text, fontSize: 13 }} />
              </div>

              {/* Кнопки оплаты — P1.3: disabled во время submit (защита от двойного списания) */}
              {!payMode && (
                <div style={{ display: 'flex', gap: 10 }}>
                  <Btn variant={isRec ? "success" : "danger"} onClick={() => doPayment(remaining)} disabled={paying} style={{ flex: 1, justifyContent: 'center', padding: 12, fontSize: 13 }}>
                    <Check size={16} /> {paying ? 'Оплата...' : S().debts.fullPayment(fmt(remaining))}
                  </Btn>
                  <Btn variant="ghost" onClick={() => setPayMode('partial')} disabled={paying} style={{ flex: 1, justifyContent: 'center', padding: 12, fontSize: 13, border: `1px solid ${color}40`, color }}>
                    {S().debts.partialPayment}
                  </Btn>
                </div>
              )}

              {/* Форма частичной оплаты */}
              {payMode === 'partial' && (
                <div className="anim-tx">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: t.text }}>{S().debts.enterPaymentAmount}</div>
                    <HelpTooltip text={HELP_TEXTS.debtPartial} size={13} />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <NumInput value={partialAmt} onChange={setPartialAmt} style={{ flex: 1 }} />
                    <Btn variant={isRec ? "success" : "danger"} onClick={() => doPayment(parseFloat(partialAmt))} disabled={!partialAmt || parseFloat(partialAmt) <= 0 || paying} style={{ padding: '10px 16px', fontSize: 13 }}>
                      <Check size={14} /> {paying ? 'Оплата...' : S().debts.payBtn}
                    </Btn>
                    <Btn variant="ghost" onClick={() => { setPayMode(null); setPartialAmt(''); }} style={{ padding: '10px 12px' }}>
                      <X size={14} />
                    </Btn>
                  </div>
                  <div style={{ fontSize: 11, color: t.textMuted, marginTop: 6 }}>
                    {S().debts.maxPaymentHint(fmt(remaining))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Если полностью оплачен */}
          {remaining <= 0 && (
            <div style={{ padding: 14, background: `${t.accentGreen}10`, borderRadius: 12, border: `1px solid ${t.accentGreen}40`, textAlign: 'center' }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: t.accentGreen }}>✅ {S().debts.fullyPaid}</div>
              <div style={{ fontSize: 12, color: t.textSec, marginTop: 4 }}>{S().debts.allPaymentsFixed}</div>
            </div>
          )}

        </div>
      </div>
    );
}



// Stage 21 follow-up (2026-05-14): InvoiceImportModal extracted из DebtsTab body
// в module-level. До этого функция определялась внутри DebtsTab — каждый re-render
// parent создавал НОВУЮ identity функции, React re-mountил модалку, file input state
// терялся. Юзеру приходилось кликать "Открыть" дважды. Сейчас стабильный module-level
// component → одна identity → без re-mount.
function InvoiceImportModal({ data, uOrg, user, apiSync, orgId, isCompact, t, onClose }) {
  const [step, setStep] = useState('upload'); // 'upload' | 'loading' | 'preview' | 'error'
  const [parsed, setParsed] = useState(null);
  const [match, setMatch] = useState(null);
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  // P1.3 (2026-05-21): synchronous submit-lock против двойного клика на «Создать».
  // saving state выставляется в save() после первых async операций, поэтому между
  // двумя быстрыми кликами он может ещё быть false → создавался бы дубликат долга.
  const submittingRef = React.useRef(false);

  // Редактируемые поля превью
  const [debtType, setDebtType] = useState('');
  const [amount, setAmount] = useState('');
  const [accountId, setAccountId] = useState('');
  const [fundId, setFundId] = useState((data.funds || []).filter(f => !f.archived)[0]?.id || '');
  const [categoryId, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [cpName, setCpName] = useState('');
  const [cpLegalName, setCpLegalName] = useState('');
  const [cpBin, setCpBin] = useState('');
  const [cpIban, setCpIban] = useState('');
  const [contractorId, setContractorId] = useState('');
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0,10));
  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 7);
  const [dueDate, setDueDate] = useState(tomorrow.toISOString().slice(0,10));

  const handleFile = async (e) => {
    const f = e.target.files[0];
    // Сбрасываем value сразу — иначе повторный выбор того же файла (или того же
    // имени) не триггерит onChange, и нужен 2-й клик. Классический баг input[type=file].
    const inputEl = e.target;
    if (f) { try { inputEl.value = ''; } catch {} }
    if (!f) return;
    if (f.type !== 'application/pdf') { setError(S().debts.onlyPdfSupported); setStep('error'); return; }
    if (f.size > 5 * 1024 * 1024) { setError('Файл больше 5 МБ. Загрузите файл поменьше.'); setStep('error'); return; }
    setFile(f);
    setStep('loading');
    setError('');

    try {
      const parsedData = await parseInvoice(f);
      setParsed(parsedData);

      const matchResult = matchInvoiceToOrg(parsedData, data.accounts || [], data.contractors || []);
      setMatch(matchResult);

      // Заполняем поля из распознанных данных
      setDebtType(matchResult.type || '');
      setAmount(String(parsedData.amount || ''));
      setAccountId(matchResult.matchedAccountId || (data.accounts || []).filter(a => !a.archived)[0]?.id || '');
      setContractorId(matchResult.matchedContractorId || '');

      if (parsedData.docDate) setIssueDate(parsedData.docDate);
      if (parsedData.docDueDate) setDueDate(parsedData.docDueDate);

      const desc = parsedData.description ||
        (parsedData.docType === 'invoice' ? S().debts.invoiceDesc(parsedData.docNumber) :
         parsedData.docType === 'payment_order' ? S().debts.paymentOrderDesc(parsedData.docNumber) : '');
      setDescription(desc);

      if (matchResult.counterparty) {
        setCpName(matchResult.counterparty.name || '');
        setCpLegalName(matchResult.counterparty.name || '');
        setCpBin(matchResult.counterparty.bin || '');
        setCpIban(matchResult.counterparty.iban || '');
      }

      setStep('preview');
    } catch (err) {
      console.warn('Invoice parse error:', err);
      setError(S().debts.pdfReadError);
      setStep('error');
    }
  };

  const isPayable = debtType === 'payable';
  const activeCats = isPayable ? (data.expenseCategories || []) : (data.incomeCategories || []);

  const save = async () => {
    // P1.3: synchronous guard — мгновенная защита от двойного клика.
    if (submittingRef.current) return;

    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { alert(S().debts.correctAmountAlert); return; }
    if (!debtType) { alert(S().debts.selectDebtType); return; }
    if (!accountId) { alert(S().debts.selectAccount); return; }

    // Все валидации пройдены — лочим submit (saving state выставится ниже после async).
    submittingRef.current = true;

    let finalContractorId = contractorId;
    let newContractorPayload = null;

    // Создаём нового контрагента если нужно
    if (!finalContractorId && cpName.trim()) {
      const newId = `c_${uid()}`;
      newContractorPayload = {
        id: newId,
        name: cpName.trim(),
        legalName: cpLegalName.trim() || cpName.trim(),
        bin: cpBin.trim(),
        phone: '', email: '', address: '',
        isCustomer: !isPayable,
        isSupplier: isPayable,
        accountIds: [], categoryIds: [], fundIds: [], expCatIds: [],
        archived: false,
      };
      uOrg(p => ({ ...p, contractors: [...(p.contractors||[]), newContractorPayload] }));
      finalContractorId = newId;

      // Fix (2026-06-09): ждём контрагента и берём ЕГО id (существующего или нового). Backend
      // при дубле возвращает существующего → используем его id для долга (иначе POST /debts 404).
      if (apiSync) {
        const created = await apiSync.syncCreateContractor(newContractorPayload);
        if (created && created.id) finalContractorId = created.id;
      }

      addAuditLog(uOrg, user, {
        type: 'contractor_create',
        desc: `Контрагент из счёта: ${cpName.trim()}`,
        details: `БИН: ${cpBin}. Создан автоматически при импорте документа.`
      });
    }

    if (!finalContractorId) {
      // P1.3: validation provалилась после async-work выше — сбрасываем lock чтобы
      // юзер мог повторить после указания контрагента.
      submittingRef.current = false;
      alert(S().debts.specifyContractor);
      return;
    }

    const fallbackCatId = activeCats.length > 0 ? activeCats[0].id : '';

    setSaving(true);
    // Загружаем PDF на сервер
    let attachmentPath = null;
    let attachmentName = null;
    try {
      if (file && orgId) {
        const uploaded = await api.uploadDebtAttachment(orgId, file);
        attachmentPath = uploaded.attachmentPath;
        attachmentName = uploaded.attachmentName;
      }
    } catch (err) {
      // P1.3: ошибка загрузки PDF — сбрасываем lock и saving чтобы юзер мог повторить.
      submittingRef.current = false;
      setSaving(false);
      alert(S().common.pdfUploadError + ': ' + (err.message || S().common.unknownError));
      return;
    }

    const newDebt = {
      id: `debt_${uid()}`, type: debtType, contractorId: finalContractorId, amount: amt,
      issueDate, date: dueDate, description,
      isRegular: false, frequency: 'month', status: 'active',
      accountId, fundId: isPayable ? fundId : '', categoryId: categoryId || fallbackCatId,
      attachmentPath,
      attachmentName,
    };

    uOrg(p => ({ ...p, debts: [...(p.debts||[]), newDebt] }));

    // Task #52 (2026-05-25): R7 fix — ждём пока debt запишется в БД ПРЕЖДЕ чем
    // закрыть модал. Иначе юзер быстро открывает только что созданный debt и
    // платит — POST /operations прилетает раньше POST /debts → 404 cross-org check
    // → operation не создаётся. С await debt гарантированно в БД когда модал закроется.
    if (apiSync) {
      try {
        await apiSync.syncCreateDebt({
          ...newDebt,
          amount: amt,
          type: debtType,
          attachmentPath,
          attachmentName,
        });
      } catch (err) {
        // syncCreateDebt уже показал alert через handleSyncError. Сбрасываем lock,
        // модал не закрываем — юзер видит сообщение и может повторить.
        submittingRef.current = false;
        setSaving(false);
        return;
      }
    }

    addAuditLog(uOrg, user, {
      type: 'debt_create',
      desc: S().debts.auditImportDebt(isPayable ? S().debts.auditWeOwe : S().debts.auditTheyOwe, fmt(amt)),
      details: `${description}. Файл: ${file.name}. Контрагент: ${cpName || (data.contractors||[]).find(c=>c.id===finalContractorId)?.name}`
    });

    setSaving(false);
    onClose();
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.6)',zIndex:1100,display:'flex',alignItems:'center',justifyContent:'center',padding:16}} onMouseDown={e => { if (e.target === e.currentTarget) (()=>onClose())(); }}>
      <div onClick={e=>e.stopPropagation()} style={{background:t.bgCard,border:`2px solid ${t.accent}`,borderRadius:16,padding: isCompact ? 16 : 24,width:560,maxWidth:'95vw',maxHeight:'90vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,.4)'}}>

        {/* Шапка */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <div style={{width:36,height:36,borderRadius:10,background:`${t.accent}15`,color:t.accent,display:'flex',alignItems:'center',justifyContent:'center'}}><Upload size={20}/></div>
            <div>
              <div style={{fontSize:17,fontWeight:800}}>{S().debts.uploadInvoice}</div>
              <div style={{fontSize:12,color:t.textMuted}}>{S().debts.importFromPdf}</div>
            </div>
          </div>
          <button onClick={()=>onClose()} style={{background:'#fee2e2',border:'2px solid #b91c1c',borderRadius:10,padding:5,color:'#b91c1c',cursor:'pointer'}}><X size={18}/></button>
        </div>

        {/* === ШАГ 1: ЗАГРУЗКА === */}
        {step === 'upload' && (
          <div>
            <label style={{
              display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:12,
              padding:40,borderRadius:14,border:`2px dashed ${t.border}`,background:t.bgInput,cursor:'pointer',
              transition:'0.2s',
            }}
            onDragOver={e=>{e.preventDefault();e.currentTarget.style.borderColor=t.accent}}
            onDragLeave={e=>{e.currentTarget.style.borderColor=t.border}}
            onDrop={e=>{e.preventDefault();e.currentTarget.style.borderColor=t.border;const f=e.dataTransfer.files[0];if(f){const dt=new DataTransfer();dt.items.add(f);const inp=e.currentTarget.querySelector('input');inp.files=dt.files;inp.dispatchEvent(new Event('change',{bubbles:true}))}}}
            >
              <FileText size={40} color={t.textMuted}/>
              <div style={{fontSize:14,fontWeight:700,color:t.text}}>{S().debts.dragDropPdf}</div>
              <div style={{fontSize:12,color:t.textMuted}}>{S().debts.invoiceOrPaymentOrder}</div>
              <div style={{fontSize:11,color:t.textMuted,marginTop:4}}>Макс. размер 5 МБ. PDF хранится на сервере до 90 дней после оплаты.</div>
              <input type="file" accept=".pdf" onChange={handleFile} style={{display:'none'}}/>
            </label>
          </div>
        )}

        {/* === ЗАГРУЗКА === */}
        {step === 'loading' && (
          <div style={{textAlign:'center',padding:40}}>
            <Loader size={32} color={t.accent} style={{animation:'spin 1s linear infinite'}}/>
            <div style={{marginTop:12,fontSize:14,fontWeight:600,color:t.text}}>{S().debts.recognizingDoc}</div>
            <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
          </div>
        )}

        {/* === ОШИБКА === */}
        {step === 'error' && (
          <div>
            <div style={{padding:16,background:`${t.accentRed}10`,borderRadius:12,border:`1px solid ${t.accentRed}30`,display:'flex',alignItems:'center',gap:10,marginBottom:16}}>
              <AlertCircle size={20} color={t.accentRed}/>
              <div style={{fontSize:13,fontWeight:600,color:t.accentRed}}>{error}</div>
            </div>
            <Btn variant="ghost" onClick={()=>{setStep('upload');setError('')}} style={{width:'100%',justifyContent:'center',padding:12}}>{S().debts.tryAgain}</Btn>
          </div>
        )}

        {/* === ШАГ 2: ПРЕВЬЮ === */}
        {step === 'preview' && parsed && (
          <div>
            {/* Распознанный документ */}
            <div style={{padding:12,background:`${t.accentGreen}08`,borderRadius:10,border:`1px solid ${t.accentGreen}30`,marginBottom:16}}>
              <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:6}}>
                <FileText size={14} color={t.accentGreen}/>
                <span style={{fontSize:12,fontWeight:700,color:t.accentGreen}}>
                  {parsed.docType === 'invoice' ? S().debts.invoiceDocType : parsed.docType === 'payment_order' ? S().debts.paymentOrderDocType : S().debts.documentDocType}
                  {parsed.docNumber ? ` №${parsed.docNumber}` : ''}
                </span>
              </div>
              <div style={{fontSize:11,color:t.textMuted}}>{file.name}</div>
            </div>

            {/* Тип обязательства */}
            <div style={{marginBottom:14}}>
              <label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:6,display:'block'}}>{S().debts.debtTypeLabel}</label>
              <div style={{display:'flex',gap:8}}>
                <button onClick={()=>setDebtType('receivable')} style={{
                  flex:1,padding:'10px 12px',borderRadius:10,fontSize:13,fontWeight:700,cursor:'pointer',
                  border:`2px solid ${debtType==='receivable'?t.accentGreen:t.border}`,
                  background:debtType==='receivable'?`${t.accentGreen}15`:t.bgInput,
                  color:debtType==='receivable'?t.accentGreen:t.text,
                }}>{S().debts.receivableShort}</button>
                <button onClick={()=>setDebtType('payable')} style={{
                  flex:1,padding:'10px 12px',borderRadius:10,fontSize:13,fontWeight:700,cursor:'pointer',
                  border:`2px solid ${debtType==='payable'?t.accentRed:t.border}`,
                  background:debtType==='payable'?`${t.accentRed}15`:t.bgInput,
                  color:debtType==='payable'?t.accentRed:t.text,
                }}>{S().debts.payableShort}</button>
              </div>
              {!debtType && match && !match.type && (
                <div style={{fontSize:11,color:t.accentOrange,marginTop:4}}>
                  {S().debts.autoDetectFailed}
                </div>
              )}
              {match && match.typeUncertain && (
                <div style={{fontSize:11,color:t.accentOrange,marginTop:4,fontWeight:600}}>
                  ⚠ {S().debts.typeUncertain}
                </div>
              )}
            </div>

            {/* Контрагент */}
            <div style={{marginBottom:14}}>
              <label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().debts.editContractorLabel}</label>
              {contractorId ? (
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <div style={{flex:1,padding:'8px 12px',borderRadius:8,background:t.bgInput,border:`1px solid ${t.accentGreen}40`,fontSize:13}}>
                    {(data.contractors||[]).find(c=>c.id===contractorId)?.name || S().debts.foundLabel}
                    <span style={{fontSize:11,color:t.accentGreen,marginLeft:6,fontWeight:700}}>{S().debts.foundByBin}</span>
                  </div>
                  <button onClick={()=>setContractorId('')} style={{background:t.bgInput,borderRadius:8,padding:6,border:`1px solid ${t.border}`,cursor:'pointer',color:t.textMuted}}><X size={14}/></button>
                </div>
              ) : (
                <div>
                  <div style={{fontSize:11,color:t.accentOrange,marginBottom:6,fontWeight:600}}>{S().debts.contractorNotFound}</div>
                  <div style={{display:'grid',gap:8}}>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                      <div>
                        <label style={{fontSize:11,color:t.textMuted,marginBottom:2,display:'block'}}>{S().debts.nameFieldLabel}</label>
                        <input value={cpName} onChange={e=>setCpName(e.target.value)} placeholder={S().debts.nameFieldLabel} style={{width:'100%',padding:'6px 10px',borderRadius:8,background:t.bgInput,color:t.text,border:`1px solid ${t.border}`,fontSize:12}}/>
                      </div>
                      <div>
                        <label style={{fontSize:11,color:t.textMuted,marginBottom:2,display:'block'}}>{S().debts.binFieldLabel}</label>
                        <input value={cpBin} onChange={e=>setCpBin(e.target.value)} placeholder={S().debts.binPlaceholder} style={{width:'100%',padding:'6px 10px',borderRadius:8,background:t.bgInput,color:t.text,border:`1px solid ${t.border}`,fontSize:12}}/>
                      </div>
                    </div>
                    <div>
                      <label style={{fontSize:11,color:t.textMuted,marginBottom:2,display:'block'}}>{S().debts.legalNameLabel}</label>
                      <input value={cpLegalName} onChange={e=>setCpLegalName(e.target.value)} placeholder={S().debts.legalNamePlaceholder} style={{width:'100%',padding:'6px 10px',borderRadius:8,background:t.bgInput,color:t.text,border:`1px solid ${t.border}`,fontSize:12}}/>
                    </div>
                  </div>
                  {/* Или выбрать существующего */}
                  <div style={{marginTop:8}}>
                    <label style={{fontSize:11,color:t.textMuted,marginBottom:2,display:'block'}}>{S().debts.orSelectExisting}</label>
                    <SearchableSelect
                      value={contractorId}
                      onChange={val => setContractorId(val)}
                      options={(data.contractors||[]).map(c => ({value:c.id, label:c.name, subLabel:c.bin}))}
                      placeholder={S().debts.searchPlaceholder}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Сумма */}
            <div style={{marginBottom:14}}>
              <label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().debts.amountLabel}</label>
              <NumInput value={amount} onChange={setAmount}/>
            </div>

            {/* Счёт и категория */}
            <div style={{display:'flex',gap:10,marginBottom:14,flexWrap:'wrap'}}>
              <div style={{flex:'1 1 45%'}}>
                <label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{isPayable ? S().debts.accountDebitLabel : S().debts.accountCreditLabelReq}</label>
                <select value={accountId} onChange={e => {
                  const val = e.target.value;
                  // P1.6: подтверждение для hidden. Импорт счёта = создание долга,
                  // оплата которого будет income (если receivable) или expense (если payable).
                  const acc = (data.accounts || []).find(a => a.id === val);
                  if (acc?.isHidden && !confirmHiddenAccountChoice(acc.name, isPayable ? 'expense' : 'income')) return;
                  setAccountId(val);
                }} style={{width:'100%',padding:8,borderRadius:8,background:t.bgInput,color:t.text,border:`1px solid ${t.border}`}}>
                  <AccountOptionsGrouped accounts={data.accounts || []} />
                </select>
              </div>
              {isPayable && (
                <div style={{flex:'1 1 45%'}}>
                  <label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().debts.fundDebitLabelReq}</label>
                  <select value={fundId} onChange={e=>setFundId(e.target.value)} style={{width:'100%',padding:8,borderRadius:8,background:t.bgInput,color:t.text,border:`1px solid ${t.border}`}}>
                    {(data.funds||[]).filter(f=>!f.archived).map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
              )}
              <div style={{flex:'1 1 45%'}}>
                <label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().debts.categoryDynamic(isPayable)}</label>
                <select value={categoryId} onChange={e=>setCategoryId(e.target.value)} style={{width:'100%',padding:8,borderRadius:8,background:t.bgInput,color:t.text,border:`1px solid ${t.border}`}}>
                  <option value="">{S().debts.selectPlaceholder}</option>
                  {activeCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>

            {/* Описание и даты */}
            <div style={{marginBottom:14}}>
              <label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().debts.descriptionImport}</label>
              <input value={description} onChange={e=>setDescription(e.target.value)} placeholder={S().debts.forWhatPlaceholder} style={{width:'100%',padding:'8px 12px',borderRadius:8,background:t.bgInput,color:t.text,border:`1px solid ${t.border}`}}/>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:20}}>
              <div>
                <label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().debts.issueDateLabel}</label>
                <DateField value={issueDate} onChange={setIssueDate} width="100%"/>
              </div>
              <div>
                <label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().debts.expectedPaymentLabel}</label>
                <DateField value={dueDate} onChange={setDueDate} width="100%"/>
              </div>
            </div>

            {/* Кнопки */}
            <div style={{display:'flex',gap:10}}>
              <Btn variant="ghost" onClick={()=>onClose()} style={{flex:1,justifyContent:'center',padding:12}} disabled={saving}>{S().common.cancel}</Btn>
              <Btn variant={isPayable ? "danger" : "success"} onClick={save} style={{flex:1,justifyContent:'center',padding:12,fontSize:14}} disabled={!debtType || saving}>
                <Check size={16}/> {saving ? 'Загрузка...' : S().debts.createDebt}
              </Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DebtsTab({ data, uOrg, user, perms, isCompact, apiSync, orgId, navDebt, onClearNavDebt }) {
  const t = useTheme();
  const [showDebtModal, setShowDebtModal] = useState(null); // 'receivable' | 'payable'
  
  // ДОБАВЬ ВОТ ЭТУ СТРОЧКУ:
  const [showContractorModal, setShowContractorModal] = useState(false);
  const [createdContractorId, setCreatedContractorId] = useState(null); // НОВОЕ: Для автоподстановки созданного
  
  // НОВОЕ: Стейты для работы логики просмотра, подсказок и новых контрагентов
  const [viewDebt, setViewDebt] = useState(null); // Для просмотра/редактирования конкретного долга
  // showHelp state removed — now using HelpTooltip component
  const [newContractorRole, setNewContractorRole] = useState(null); // 'client' | 'supplier'
  const [showInvoiceImport, setShowInvoiceImport] = useState(false); // Модалка импорта из PDF

  // P1.3.4 (2026-05-21): UPLIFTED STATE для DebtModal.
  // Корневая причина regression «контрагент исчезает через 1-2 сек, сумма обнуляется»:
  // DebtModal определён как inner functional component внутри DebtsTab → каждый ререндер
  // DebtsTab (от polling refresh, uOrg, etc.) создаёт новую reference функции DebtModal →
  // React делает unmount + mount → useState внутри сбрасывается → форма очищается.
  // Подняли всё state (форма, submit-lock, saving) сюда в DebtsTab, где они живут
  // независимо от того, пересоздаётся DebtModal или нет. Тот же паттерн нужно применить
  // к ContractorModal (тоже inner) — отдельным чанком.
  const [debtModalData, setDebtModalData] = useState(null);
  const submittingDebtRef = React.useRef(false);
  const [savingDebt, setSavingDebt] = useState(false);

  // Инициализация debtModalData при открытии модала; сброс при закрытии.
  React.useEffect(() => {
    if (showDebtModal && !debtModalData) {
      const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 7);
      setDebtModalData({
        contractorId: createdContractorId || '',
        amount: '',
        issueDate: new Date().toISOString().slice(0,10),
        date: tomorrow.toISOString().slice(0,10),
        description: '',
        isRegular: false,
        frequency: 'month',
        accountId: (data.accounts || []).filter(a => !a.archived)[0]?.id || '',
        fundId: (data.funds || []).filter(f => !f.archived)[0]?.id || '',
        categoryId: '',
        regularDay: 1,
        regularMonth: 1,
      });
    } else if (!showDebtModal && debtModalData) {
      setDebtModalData(null);
      submittingDebtRef.current = false;
      setSavingDebt(false);
    }
  }, [showDebtModal, debtModalData, createdContractorId, data.accounts, data.funds]);

  // Автоподстановка контрагента когда юзер создал нового через ContractorModal.
  React.useEffect(() => {
    if (createdContractorId && debtModalData && debtModalData.contractorId !== createdContractorId) {
      setDebtModalData(prev => prev ? { ...prev, contractorId: createdContractorId } : prev);
    }
  }, [createdContractorId, debtModalData]);

  // Task #46 (2026-05-21): UPLIFTED STATE для ContractorModal (тот же паттерн что DebtModal).
  // Превентивный фикс того же класса бага: ContractorModal — inner functional component
  // внутри DebtsTab → пересоздаётся при каждом ререндере parent → её useState теряет
  // значения. Юзер сейчас не репортит, но это произойдёт при polling refresh.
  const [contractorModalData, setContractorModalData] = useState(null);
  const [contractorModalError, setContractorModalError] = useState('');

  React.useEffect(() => {
    if (showContractorModal && !contractorModalData) {
      setContractorModalData({
        name: '', legalName: '', bin: '', phone: '', email: '', address: '',
        isCustomer: newContractorRole === 'client',
        isSupplier: newContractorRole === 'supplier',
        accountIds: [], categoryIds: [], fundIds: [], expCatIds: [],
      });
      setContractorModalError('');
    } else if (!showContractorModal && contractorModalData) {
      setContractorModalData(null);
      setContractorModalError('');
    }
  }, [showContractorModal, contractorModalData, newContractorRole]);

  // Task #42 (2026-05-21): deep-link consumer. Если App.js передал navDebt (debtId
  // на который кликнули в дашборде AFTab) — открываем ViewDebtModal для этого долга
  // и очищаем navDebt чтобы не открыть снова при следующем ререндере.
  // Single-shot pattern (тот же что navTx в TxTab).
  React.useEffect(() => {
    if (navDebt && (data.debts || []).length > 0) {
      const target = (data.debts || []).find(d => d.id === navDebt);
      if (target) {
        setViewDebt(target);
      }
      if (onClearNavDebt) onClearNavDebt();
    }
  }, [navDebt, data.debts, onClearNavDebt]);

  // Task #43 (2026-05-21): сортировки + поиск над каждой колонкой.
  // Дефолт: новые сверху (sort=created, dir=desc) — как просил юзер.
  const [recSearch, setRecSearch] = useState('');
  const [paySearch, setPaySearch] = useState('');
  const [recSort, setRecSort] = useState({ field: 'created', dir: 'desc' });
  const [paySort, setPaySort] = useState({ field: 'created', dir: 'desc' });

  // Хелпер: фильтр (по имени контрагента / БИН / сумме) + сортировка по выбранному полю.
  const filterAndSortDebts = (debts, search, sort) => {
    const s = (search || '').trim().toLowerCase();
    const contractors = data.contractors || [];
    let result = debts;
    if (s) {
      result = debts.filter(d => {
        const con = contractors.find(c => c.id === d.contractorId);
        const conName = (con?.name || '').toLowerCase();
        const bin = (con?.bin || '').toLowerCase();
        const amt = String(d.amount || '');
        return conName.includes(s) || bin.includes(s) || amt.includes(s);
      });
    }
    return [...result].sort((a, b) => {
      let cmp = 0;
      if (sort.field === 'name') {
        const aName = (contractors.find(c => c.id === a.contractorId)?.name || '').toLowerCase();
        const bName = (contractors.find(c => c.id === b.contractorId)?.name || '').toLowerCase();
        cmp = aName.localeCompare(bName, getLocale());
      } else if (sort.field === 'amount') {
        cmp = (a.amount || 0) - (b.amount || 0);
      } else {
        // 'created' — используем issueDate как proxy даты создания (отдельного createdAt в JSON нет)
        cmp = new Date(a.issueDate || a.date || 0) - new Date(b.issueDate || b.date || 0);
      }
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  };

  const activeDebts = (data.debts || []).filter(d => d.status === 'active' || d.status === 'partial');
  const receivables = filterAndSortDebts(activeDebts.filter(d => d.type === 'receivable'), recSearch, recSort);
  const payables = filterAndSortDebts(activeDebts.filter(d => d.type === 'payable'), paySearch, paySort);

  const totalReceivables = receivables.reduce((s, d) => s + (d.amount - (d.paidAmount || 0)), 0);
  const totalPayables = payables.reduce((s, d) => s + (d.amount - (d.paidAmount || 0)), 0);

  const delDebt = async (id) => {
    if (!window.confirm(S().debts.deleteConfirm)) return;
    if (!apiSync) return;
    const d = (data.debts || []).find(x => x.id === id);
    const r = await apiSync.syncDeleteDebt(id);
    if (!r) return;
    addAuditLog(uOrg, user, {
      type: 'debt_delete',
      desc: S().debts.auditDeleted(d?.description || id),
      details: S().debts.auditTypeAmount(d?.type === 'receivable' ? S().debts.auditTypeReceivable : S().debts.auditTypePayable, fmt(d?.amount))
    });
    try { window.dispatchEvent(new CustomEvent('inmonex-reload-org')); } catch (e) {}
  };

  // === ОБНОВЛЕННАЯ МОДАЛКА КОНТРАГЕНТА (Мультивыбор тегами как в Справочнике + Заблокированные роли) ===
  const ContractorModal = ({ onSaveNew }) => {
    // Task #46 (2026-05-21): UPLIFTED STATE. useState поднят в parent (contractorModalData,
    // contractorModalError) чтобы значения не терялись при ререндере DebtsTab. Внутри
    // ContractorModal больше нет hooks → её можно вызывать как функцию через {ContractorModal()}
    // (inline JSX → React reuse DOM nodes → focus preserved при вводе).
    const c = contractorModalData || {
      name: '', legalName: '', bin: '', phone: '', email: '', address: '',
      isCustomer: false, isSupplier: false,
      accountIds: [], categoryIds: [], fundIds: [], expCatIds: [],
    };
    const setC = (updater) => {
      setContractorModalData(prev => {
        if (!prev) return prev;
        return typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      });
    };
    const error = contractorModalError;
    const setError = setContractorModalError;

    const save = () => {
      setError('');
      if (!c.name.trim()) { setError(S().debts.enterContractorName); return; }
      if (!c.legalName?.trim()) { setError(S().debts.enterLegalName); return; }
      if (!c.bin?.trim()) { setError(S().debts.enterBin); return; }
      if (!c.isCustomer && !c.isSupplier) { setError(S().debts.selectContractorRole); return; }
      
      // ПРОВЕРКА НА ДУБЛИКАТ (БИН или Юр. название)
      const curBin = String(c.bin || '').trim().toLowerCase();
      const curLegal = String(c.legalName || '').trim().toLowerCase();

      const exists = (data.contractors || []).find(x => 
        (x.bin && String(x.bin).trim().toLowerCase() === curBin && curBin !== '') || 
        (x.legalName && String(x.legalName).trim().toLowerCase() === curLegal && curLegal !== '')
      );
      if (exists) {
        setError(S().debts.duplicateContractor(c.bin, exists.name));
        return;
      }

      const newId = `c_${uid()}`;
      const newContractor = { ...c, id: newId };
      // Сохраняем контрагента со всеми массивами привязок
      uOrg(p => ({ ...p, contractors: [...(p.contractors||[]), newContractor] }));
      // Чанк 20a.5b: persist в БД через REST (Inline ContractorModal в DebtsTab).
      if (apiSync && apiSync.syncCreateContractor) apiSync.syncCreateContractor(newContractor);
      if (onSaveNew) onSaveNew(newId);
      setShowContractorModal(false);
    };

    return (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.6)',zIndex:1200,display:'flex',alignItems:'center',justifyContent:'center'}} onMouseDown={e => { if (e.target === e.currentTarget) (()=>{setShowContractorModal(false);})(); }}>
        {/* Сделали ширину 600px как в оригинальном справочнике для удобства тегов */}
        <div onClick={e=>e.stopPropagation()} style={{background:t.bgCard,border:`1px solid ${t.border}`,borderRadius:16,padding: isCompact ? 16 : 24,width:600,maxWidth:'95vw',maxHeight:'90vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,.4)'}}>

          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <div style={{fontSize: isCompact ? 16 : 18,fontWeight:800}}>{S().debts.newContractorTitle}</div>
              <HelpTooltip text={HELP_TEXTS.contractor} size={15} />
            </div>
            <button onClick={()=>{setShowContractorModal(false);}} style={{background:'#fee2e2',border:'2px solid #b91c1c',borderRadius:10,padding:5,color:'#b91c1c',cursor:'pointer'}}><X size={18}/></button>
          </div>


          
          {/* Галочки ролей теперь доступны для выбора */}
          <div style={{display:'flex', gap:16, marginBottom: 20, padding:14, background:t.bgInput, borderRadius:12, border:`1px solid ${t.border}`}}>
            <label style={{display:'flex', alignItems:'center', gap:8, cursor:'pointer'}}>
              <input type="checkbox" checked={c.isCustomer} onChange={e=>setC({...c, isCustomer: e.target.checked})} style={{width:16, height:16, cursor:'pointer'}}/>
              <span style={{fontSize: 13, fontWeight: 700, color: t.accentGreen}}>{S().debts.clientLabel}</span>
            </label>
            <label style={{display:'flex', alignItems:'center', gap:8, cursor:'pointer'}}>
              <input type="checkbox" checked={c.isSupplier} onChange={e=>setC({...c, isSupplier: e.target.checked})} style={{width:16, height:16, cursor:'pointer'}}/>
              <span style={{fontSize: 13, fontWeight: 700, color: t.accentRed}}>{S().debts.supplierLabel}</span>
            </label>
          </div>

          <div style={{display:'grid',gap:12,marginBottom:24}}>
            <div><label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().debts.nameForYouLabel}</label><input value={c.name} onChange={e=>setC({...c,name:e.target.value})} placeholder={S().debts.namePlaceholder} style={{width:'100%', padding:'8px 12px', borderRadius:8, background:t.bgInput, color:t.text, border:`1px solid ${t.border}`}}/></div>
            <div style={{display:'grid',gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr',gap:12}}>
              <div><label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().debts.legalNameLabelReq}</label><input value={c.legalName} onChange={e=>setC({...c,legalName:e.target.value})} placeholder={S().debts.legalNamePlaceholder} style={{width:'100%', padding:'8px 12px', borderRadius:8, background:t.bgInput, color:t.text, border:`1px solid ${t.border}`}}/></div>
              <div><label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().debts.binLabelReq}</label><input value={c.bin} onChange={e=>setC({...c,bin:e.target.value})} placeholder={S().debts.binPlaceholder} style={{width:'100%', padding:'8px 12px', borderRadius:8, background:t.bgInput, color:t.text, border:`1px solid ${t.border}`}}/></div>
            </div>
            <div style={{display:'grid',gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr',gap:12}}>
              <div><label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().debts.phoneLabel}</label><input value={c.phone} onChange={e=>setC({...c,phone:e.target.value})} placeholder={S().debts.phonePlaceholder} style={{width:'100%', padding:'8px 12px', borderRadius:8, background:t.bgInput, color:t.text, border:`1px solid ${t.border}`}}/></div>
              <div><label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().debts.emailLabel}</label><input value={c.email} onChange={e=>setC({...c,email:e.target.value})} placeholder={S().debts.emailPlaceholder} style={{width:'100%', padding:'8px 12px', borderRadius:8, background:t.bgInput, color:t.text, border:`1px solid ${t.border}`}}/></div>
            </div>
            <div><label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().debts.addressLabel}</label><input value={c.address} onChange={e=>setC({...c,address:e.target.value})} placeholder={S().debts.addressPlaceholder} style={{width:'100%', padding:'8px 12px', borderRadius:8, background:t.bgInput, color:t.text, border:`1px solid ${t.border}`}}/></div>
            
            {/* ПРИВЯЗКИ ТОЧНО КАК В СПРАВОЧНИКЕ */}
            <div style={{gridColumn: '1 / -1'}}>
              {c.isCustomer && (
                <div style={{marginBottom: 16, padding: 12, background: t.bgInput, borderRadius: 10, border: `1px solid ${t.accentGreen}30`}}>
                  <div style={{fontSize: 13, fontWeight: 700, marginBottom: 8}}>{S().debts.availableAccounts}</div>
                  <div style={{fontSize: 11, color: t.textMuted, marginBottom: 8}}>{S().debts.accountsHint}</div>
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
                    {(data.accounts||[]).filter(a => !a.archived).map(a => (
                      <button type="button" key={a.id} onClick={() => setC(p => ({ ...p, accountIds: p.accountIds?.includes(a.id) ? p.accountIds.filter(x => x !== a.id) : [...(p.accountIds||[]), a.id] }))} style={{padding: '4px 10px', fontSize: 12, borderRadius: 14, background: c.accountIds?.includes(a.id) ? a.color : t.bgCard, color: c.accountIds?.includes(a.id) ? '#fff' : t.text, border: `1px solid ${a.color}`, cursor: 'pointer'}}>{a.name}</button>
                    ))}
                  </div>
                  
                  <div style={{fontSize: 12, fontWeight: 600, marginTop: 12, marginBottom: 6, color: t.accentGreen}}>{S().debts.incomeCategories}</div>
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
                    {(data.incomeCategories || []).filter(cat => !cat.archived).map(cat => (
                      <button type="button" key={cat.id} onClick={() => setC(p => ({ ...p, categoryIds: p.categoryIds?.includes(cat.id) ? p.categoryIds.filter(x => x !== cat.id) : [...(p.categoryIds||[]), cat.id] }))} style={{padding: '4px 10px', fontSize: 12, borderRadius: 14, background: c.categoryIds?.includes(cat.id) ? t.accentGreen : t.bgCard, color: c.categoryIds?.includes(cat.id) ? '#fff' : t.text, border: `1px solid ${t.accentGreen}60`, cursor: 'pointer'}}>{cat.name}</button>
                    ))}
                    {(data.incomeCategories || []).filter(cat => !cat.archived).length === 0 && <span style={{fontSize: 11, color: t.textMuted}}>{S().debts.noIncomeCategories}</span>}
                  </div>
                </div>
              )}
              {c.isSupplier && (
                <div style={{marginBottom: 8, padding: 12, background: t.bgInput, borderRadius: 10, border: `1px solid ${t.accentRed}30`}}>
                  <div style={{fontSize: 13, fontWeight: 700, marginBottom: 8}}>{S().debts.availableFunds}</div>
                  <div style={{fontSize: 11, color: t.textMuted, marginBottom: 8}}>{S().debts.fundsHint}</div>
                  <div style={{fontSize: 12, fontWeight: 600, marginTop: 10, marginBottom: 6}}>{S().debts.fundsLabel}</div>
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
                    {(data.funds||[]).filter(f => !f.archived).map(f => (
                      <button type="button" key={f.id} onClick={() => setC(p => ({ ...p, fundIds: p.fundIds?.includes(f.id) ? p.fundIds.filter(x => x !== f.id) : [...(p.fundIds||[]), f.id] }))} style={{padding: '4px 10px', fontSize: 12, borderRadius: 14, background: c.fundIds?.includes(f.id) ? f.color : t.bgCard, color: c.fundIds?.includes(f.id) ? '#fff' : t.text, border: `1px solid ${f.color}`, cursor: 'pointer'}}>{f.name}</button>
                    ))}
                  </div>
                  <div style={{fontSize: 12, fontWeight: 600, marginTop: 12, marginBottom: 6}}>{S().debts.expenseCategories}</div>
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
                    {(data.expenseCategories || []).filter(cat=>!cat.archived).map(cat => {
                      const fund = (data.funds||[]).find(f => f.id === cat.fundId);
                      return (
                        <button type="button" key={cat.id} onClick={() => setC(p => ({ ...p, expCatIds: p.expCatIds?.includes(cat.id) ? p.expCatIds.filter(x => x !== cat.id) : [...(p.expCatIds||[]), cat.id], categoryIds: p.categoryIds?.includes(cat.id) ? p.categoryIds.filter(x => x !== cat.id) : [...(p.categoryIds||[]), cat.id] }))} style={{padding: '4px 10px', fontSize: 12, borderRadius: 14, background: c.expCatIds?.includes(cat.id) ? t.accent : t.bgCard, color: c.expCatIds?.includes(cat.id) ? '#fff' : t.text, border: `1px solid ${t.border}`, cursor: 'pointer'}}>
                          {cat.name} <span style={{fontSize: 10, opacity: 0.7}}>({fund ? fund.name : '?'})</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {error && (
            <div style={{padding:'12px 16px', background:`${t.accentRed}15`, color:t.accentRed, borderRadius:12, fontSize:13, fontWeight:700, marginBottom:20, border:`1px solid ${t.accentRed}30`, display:'flex', alignItems:'center', gap:10}}>
              <X size={16}/> {error}
            </div>
          )}

          <Btn variant="success" style={{width:'100%',justifyContent:'center',padding:14,fontSize:15}} onClick={save}><Check size={18}/> {S().debts.saveContractor}</Btn>
        </div>
      </div>
    );
  };

  const DebtModal = () => {
    const isPayable = showDebtModal === 'payable';

    // P1.3.4 (2026-05-21): UPLIFTED STATE. State формы (d), submit-lock и saving живут
    // в parent DebtsTab — это защищает от потери данных при polling refresh ререндере
    // (DebtModal как inner functional component пересоздаётся каждый раз).
    // Если debtModalData ещё не инициализирован (race на самом первом mount) — fallback
    // к пустым defaults чтобы JSX не упал на d.contractorId до useEffect.
    const d = debtModalData || {
      contractorId: '', amount: '', issueDate: '', date: '', description: '',
      isRegular: false, frequency: 'month', accountId: '', fundId: '', categoryId: '',
      regularDay: 1, regularMonth: 1,
    };
    const setD = (updater) => {
      setDebtModalData(prev => {
        if (!prev) return prev; // защита от установки на null state
        return typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      });
    };
    const submittingRef = submittingDebtRef; // alias чтобы save() читалось как раньше
    const saving = savingDebt;
    const setSaving = setSavingDebt;

    // ФИЛЬТРАЦИЯ: Показываем только подходящих контрагентов
    const availableContractors = (data.contractors || []).filter(c => {
      if (isPayable) return c.isSupplier !== false;
      return c.isCustomer !== false;
    });

    // O-debt (2026-06-10): sequential — await POST, при успехе reload-org; фантома при сбое нет.
    const save = async () => {
      if (submittingRef.current) return;
      const amt = parseFloat(d.amount);
      if (!amt || !d.contractorId) { alert(S().debts.fillContractorAndAmount); return; }
      if (!d.accountId) { alert(S().debts.selectAccountForDebt); return; }
      if (isPayable && !d.fundId) { alert(S().debts.selectFundForPayable); return; }
      if (!apiSync) { alert((S().common && S().common.unknownError) || 'Нет связи с сервером'); return; }

      submittingRef.current = true;
      setSaving(true);

      const activeCats = isPayable ? (data.expenseCategories||[]) : (data.incomeCategories||[]);
      const fallbackCatId = activeCats.length > 0 ? activeCats[0].id : '';

      const newDebt = {
        id: `debt_${uid()}`, type: showDebtModal, contractorId: d.contractorId, amount: amt,
        issueDate: d.issueDate, date: d.date, description: d.description,
        isRegular: d.isRegular, frequency: d.frequency, status: 'active',
        accountId: d.accountId, fundId: d.fundId, categoryId: d.categoryId || fallbackCatId,
        regularDay: d.isRegular ? parseInt(d.regularDay) || 1 : null,
        regularMonth: d.isRegular && (d.frequency === 'quarter' || d.frequency === 'year') ? parseInt(d.regularMonth) || 1 : null
      };

      let created = null;
      try {
        created = await apiSync.syncCreateDebt({ ...newDebt, amount: amt, type: showDebtModal === 'receivable' ? 'receivable' : 'payable' });
      } catch (e) { created = null; }
      if (!created) { submittingRef.current = false; setSaving(false); return; }

      addAuditLog(uOrg, user, {
        type: 'debt_create',
        desc: S().debts.auditNewDebt(isPayable ? S().debts.auditWeOwe : S().debts.auditTheyOwe, fmt(amt)),
        details: `${d.description}. Контрагент: ${(data.contractors||[]).find(c=>c.id===d.contractorId)?.name}. Срок: ${fmtDate(d.date)}`
      });

      submittingRef.current = false;
      setSaving(false);
      setShowDebtModal(null);
      setCreatedContractorId(null);
      try { window.dispatchEvent(new CustomEvent('inmonex-reload-org')); } catch (e) {}
    };

    return (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}} onMouseDown={e => { if (e.target === e.currentTarget) (()=>{setShowDebtModal(null); setCreatedContractorId(null);})(); }}>
        <div onClick={e=>e.stopPropagation()} style={{background:t.bgCard,border:`2px solid ${isPayable ? t.accentRed : t.accentGreen}`,borderRadius:16,padding:24,width:480,maxWidth:'90vw',boxShadow:'0 20px 60px rgba(0,0,0,.4)'}}>
          
          {/* ШАПКА — единый стиль: заголовок слева, иконки справа */}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
            <div style={{fontSize:18,fontWeight:800,color:isPayable?t.accentRed:t.accentGreen}}>
              {isPayable ? S().debts.weOweTo : S().debts.whoOwesUs}
            </div>
            <div style={{display:'flex',alignItems:'center',gap:6}}>
              <HelpTooltip text={isPayable ? HELP_TEXTS.debtPayable : HELP_TEXTS.debtReceivable} size={15} />
              <button onClick={()=>{setShowDebtModal(null); setCreatedContractorId(null);}} style={{background:'#fee2e2',border:'2px solid #b91c1c',borderRadius:10,padding:5,color:'#b91c1c',cursor:'pointer'}}><X size={18}/></button>
            </div>
          </div>


          
          <div style={{display:'grid',gap:14,marginBottom:20}}>
            {/* КОНТРАГЕНТ */}
            <div style={{zIndex: 50}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                <label style={{fontSize:12,color:t.textMuted,fontWeight:700}}>{S().debts.contractorLabel}</label>
                <button onClick={() => {
                  setNewContractorRole(isPayable ? 'supplier' : 'client');
                  setShowContractorModal(true);
                }} style={{background:'transparent',color:t.accent,fontSize:12,fontWeight:700, border:'none', cursor:'pointer'}}>{S().debts.newContractor}</button>
              </div>
              <SearchableSelect 
                value={d.contractorId} 
                onChange={val => setD({...d,contractorId:val})} 
                options={availableContractors.map(c => ({value: c.id, label: c.name, subLabel: c.bin}))}
                placeholder={S().debts.selectPlaceholder}
              />
            </div>

            {/* НОВОЕ: ПРИВЯЗКИ (Счета/Фонды и Категории) */}
            {!isPayable ? (
              // Дебиторка (Нам должны) -> Счет и Доходы
              <div style={{display: 'flex', gap: 12}}>
                <div style={{flex: 1}}>
                  <label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().debts.accountCreditLabelReq}</label>
                  <select value={d.accountId} onChange={e => {
                    const val = e.target.value;
                    // P1.6: подтверждение при выборе скрытого счёта (receivable = долг нам → оплата = income).
                    const acc = (data.accounts || []).find(a => a.id === val);
                    if (acc?.isHidden && !confirmHiddenAccountChoice(acc.name, 'income')) return;
                    setD({...d, accountId: val});
                  }} style={{width:'100%', padding: 8, borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}`}}>
                    <AccountOptionsGrouped accounts={data.accounts || []} />
                  </select>
                </div>
                <div style={{flex: 1}}>
                  <label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().debts.incomeCategoryLabel}</label>
                  <select value={d.categoryId} onChange={e=>setD({...d,categoryId:e.target.value})} style={{width:'100%', padding: 8, borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}`}}>
                    {(data.incomeCategories||[]).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
            ) : (
              // Кредиторка (Мы должны) -> Счёт, Фонд и Расходы
              <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                <div style={{flex: '1 1 30%'}}>
                  <label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().debts.accountDebitLabel}</label>
                  <select value={d.accountId} onChange={e => {
                    const val = e.target.value;
                    // P1.6: подтверждение при выборе скрытого (payable = мы должны → оплата = expense).
                    const acc = (data.accounts || []).find(a => a.id === val);
                    if (acc?.isHidden && !confirmHiddenAccountChoice(acc.name, 'expense')) return;
                    setD({...d, accountId: val});
                  }} style={{width:'100%', padding: 8, borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}`}}>
                    <AccountOptionsGrouped accounts={data.accounts || []} />
                  </select>
                </div>
                <div style={{flex: '1 1 30%'}}>
                  <label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().debts.fundDebitLabelReq}</label>
                  <select value={d.fundId} onChange={e=>setD({...d,fundId:e.target.value})} style={{width:'100%', padding: 8, borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}`}}>
                    {(data.funds||[]).filter(f=>!f.archived).map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                    {(data.funds||[]).filter(f=>!f.archived).length === 0 && <option value="">{S().debts.noActiveFunds}</option>}
                  </select>
                </div>
                <div style={{flex: '1 1 30%'}}>
                  <label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().debts.expenseCategoryLabel}</label>
                  <select value={d.categoryId} onChange={e=>setD({...d,categoryId:e.target.value})} style={{width:'100%', padding: 8, borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}`}}>
                    {(data.expenseCategories||[]).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
            )}
            
            {/* СУММА */}
            <div><label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().debts.amountLabel}</label><NumInput value={d.amount} onChange={v=>setD({...d,amount:v})}/></div>

            {/* ДАТЫ */}
            <div style={{display:'grid',gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr',gap:12}}>
              <div><label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().debts.issueDateLabel}</label><DateField value={d.issueDate} onChange={v=>setD({...d,issueDate:v})} width="100%"/></div>
              <div><label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().debts.expectedPaymentLabel}</label><DateField value={d.date} onChange={v=>setD({...d,date:v})} width="100%"/></div>
            </div>

            {/* ОПИСАНИЕ */}
            <div><label style={{fontSize:12,color:t.textMuted,fontWeight:700,marginBottom:4,display:'block'}}>{S().debts.descriptionLabel}</label><input value={d.description} onChange={e=>setD({...d,description:e.target.value})} placeholder={S().debts.descriptionPlaceholder} style={{width:'100%', padding: '8px 12px', borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}`}}/></div>
            
            {/* РЕГУЛЯРНЫЙ ПЛАТЕЖ — временно скрыт (27.05.2026): после Fix #5C
                регулярные платежи показали 500 при повторной оплате (SESSION_2026_05_27).
                Существующие regular долги в БД продолжают работать (backend logic не тронут),
                новые создавать нельзя. Чтобы вернуть UI — убрать `false &&` ниже. */}
            {false && isPayable && (
              <div style={{padding:12,background:t.bgInput,borderRadius:10,border:`1px solid ${t.border}`}}>
                <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
                  <input type="checkbox" checked={d.isRegular} onChange={e=>setD({...d,isRegular:e.target.checked})} style={{width:16,height:16}}/>
                  <span style={{fontSize:13,fontWeight:700}}>{S().debts.regularPayment}</span>
                </label>
                {d.isRegular && (
                  <div style={{marginTop:10, display:'flex', gap:10, flexWrap:'wrap'}}>
                    <div style={{flex: 1, minWidth: 120}}>
                      <label style={{fontSize:12,color:t.textMuted,marginBottom:4,display:'block'}}>{S().debts.repeatLabel}</label>
                      <select value={d.frequency} onChange={e=>setD({...d,frequency:e.target.value})} style={{width:'100%', padding: 8, borderRadius: 8, background: t.bgCard, color: t.text, border: `1px solid ${t.border}`}}>
                        <option value="month">{S().debts.frequencyMonth}</option>
                        <option value="quarter">{S().debts.frequencyQuarter}</option>
                        <option value="year">{S().debts.frequencyYear}</option>
                      </select>
                    </div>
                    {['quarter', 'year'].includes(d.frequency) && (
                      <div style={{flex: 1, minWidth: 120}}>
                        <label style={{fontSize:12,color:t.textMuted,marginBottom:4,display:'block'}}>{d.frequency === 'year' ? S().debts.monthLabel : S().debts.quarterMonthLabel}</label>
                        <select value={d.regularMonth} onChange={e=>setD({...d,regularMonth:parseInt(e.target.value)||1})} style={{width:'100%', padding: 8, borderRadius: 8, background: t.bgCard, color: t.text, border: `1px solid ${t.border}`}}>
                          {d.frequency === 'year' ? (
                            <>{S().months.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}</>
                          ) : (
                            <><option value={1}>{S().debts.quarterFirst}</option><option value={2}>{S().debts.quarterSecond}</option><option value={3}>{S().debts.quarterThird}</option></>
                          )}
                        </select>
                      </div>
                    )}
                    <div style={{flex: 1, minWidth: 120}}>
                      <label style={{fontSize:12,color:t.textMuted,marginBottom:4,display:'block'}}>{S().debts.dayOfMonthLabel}</label>
                      <input type="number" min="1" max="31" value={d.regularDay} onChange={e=>setD({...d,regularDay:parseInt(e.target.value)||1})} style={{width:'100%', padding: 8, borderRadius: 8, background: t.bgCard, color: t.text, border: `1px solid ${t.border}`}} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* КНОПКА СОХРАНЕНИЯ — P1.3: disabled во время submit для защиты от дубликатов */}
          <Btn variant={isPayable?"danger":"success"} style={{width:'100%',justifyContent:'center',padding:14}} onClick={save} disabled={saving}>{saving ? 'Сохранение...' : S().debts.saveDebt}</Btn>
        </div>
      </div>
    );
  };

  const getDaysBadge = (issueDateStr) => {
    if (!issueDateStr) return null;
    const issueDate = new Date(issueDateStr); issueDate.setHours(0,0,0,0);
    const today = new Date(); today.setHours(0,0,0,0);
    const diffDays = Math.ceil((today - issueDate) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return null; 
    let color, bg;
    if (diffDays <= 5) { color = t.accentGreen; bg = `${t.accentGreen}15`; }
    else if (diffDays <= 10) { color = t.accentYellow; bg = `${t.accentYellow}15`; }
    else if (diffDays <= 20) { color = t.accentRed; bg = `${t.accentRed}15`; }
    else { color = '#b91c1c'; bg = '#b91c1c15'; } 
    return <span style={{fontSize:11, fontWeight:800, color, background:bg, padding:'2px 6px', borderRadius:6, marginLeft:6}}>{S().debts.daysPassed(diffDays)}</span>;
  };


  // Компактный рендер строки долга
  // Task #43 (2026-05-21): UI controls (поиск + sort tablets) над каждой колонкой долгов.
  // Принимает state/setState каждой колонки + accent цвет (зелёный для receivables, красный
  // для payables) — чтобы активная sort-таблетка совпадала с цветом колонки.
  const renderDebtListControls = (search, setSearch, sort, setSort, accentColor) => {
    // Клик на таблетку: если уже активна — переключаем direction. Если нет — активируем поле
    // с дефолтным направлением (по сумме/имени — asc, по дате — desc «новые сверху»).
    const toggleSort = (field) => {
      if (sort.field === field) {
        setSort({ field, dir: sort.dir === 'asc' ? 'desc' : 'asc' });
      } else {
        setSort({ field, dir: field === 'created' ? 'desc' : 'asc' });
      }
    };
    const arrow = (field) => sort.field === field ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '';
    const tabletStyle = (active) => ({
      padding: '6px 10px',
      borderRadius: 8,
      fontSize: 12,
      fontWeight: 700,
      cursor: 'pointer',
      border: `1px solid ${active ? accentColor : t.border}`,
      background: active ? `${accentColor}15` : t.bgCard,
      color: active ? accentColor : t.textMuted,
      transition: 'all 0.15s',
      whiteSpace: 'nowrap',
    });
    return (
      <div style={{ marginBottom: 14 }}>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Поиск: имя контрагента, БИН или сумма"
          style={{
            width: '100%', padding: '9px 12px', borderRadius: 10,
            background: t.bgInput, color: t.text, border: `1px solid ${t.border}`,
            fontSize: 13, marginBottom: 8, boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, marginRight: 2 }}>Сортировка:</span>
          <button type="button" onClick={() => toggleSort('created')} style={tabletStyle(sort.field === 'created')}>
            Дата{arrow('created')}
          </button>
          <button type="button" onClick={() => toggleSort('amount')} style={tabletStyle(sort.field === 'amount')}>
            Сумма{arrow('amount')}
          </button>
          <button type="button" onClick={() => toggleSort('name')} style={tabletStyle(sort.field === 'name')}>
            А-Я{arrow('name')}
          </button>
        </div>
      </div>
    );
  };

  const renderDebtLine = (d) => {
    const isRec = d.type === 'receivable';
    const color = isRec ? t.accentGreen : t.accentRed;
    const con = (data.contractors || []).find(c => c.id === d.contractorId);
    const paidAmount = d.paidAmount || 0;
    const remaining = d.amount - paidAmount;
    const isPartial = paidAmount > 0 && remaining > 0;
    
    return (
      <div 
        key={d.id} 
        onClick={() => setViewDebt(d)}
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
          padding: '12px 16px', cursor: 'pointer',
          background: t.bgInput, borderRadius: 8, marginBottom: 8, transition: '0.2s',
          border: isPartial ? `1px solid ${t.accentOrange}40` : `1px solid transparent`,
        }}
        onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'}
        onMouseLeave={e => e.currentTarget.style.filter = 'none'}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}15`, color: color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <User size={18} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{con ? con.name : S().common.unknown}</div>
            <div style={{ fontSize: 12, color: t.textMuted }}>
              {d.description || S().common.noDescription}
              {isPartial && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: t.accentOrange, background: `${t.accentOrange}15`, padding: '1px 6px', borderRadius: 4 }}>
                {S().debts.paidPercent(Math.round((paidAmount / d.amount) * 100))}
              </span>}
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="mono" style={{ fontWeight: 800, fontSize: 15, color }}>
            {fmt(remaining)}
          </div>
          {isPartial && <div style={{ fontSize: 10, color: t.textMuted }}>{S().debts.ofAmount} {fmt(d.amount)}</div>}
        </div>
      </div>
    );
  };


  return (
    <div>
      {/* Task #46: ContractorModal как inline-вызов (тот же фикс что для DebtModal в P1.3.5)
          — JSX встраивается в parent tree → React reuse DOM input nodes → focus preserved при вводе.
          Работает потому что ContractorModal теперь не имеет hooks (state поднят в parent). */}
      {showContractorModal && ContractorModal({ onSaveNew: setCreatedContractorId })}
      {/* P1.3.5 (2026-05-21): DebtModal как inline функция-вызов вместо <DebtModal/>.
          Причина: const DebtModal = () => {...} внутри DebtsTab пересоздаётся при каждом
          ререндере (новая function reference) → JSX <DebtModal/> создаёт element с НОВЫМ
          type → React reconciler делает unmount + mount → DOM input удаляется → focus
          теряется. Юзер не мог печатать больше одного символа подряд.
          Вызов DebtModal() как функции встраивает её JSX прямо в parent tree → reconciler
          видит <input> в той же position → reuse DOM node → focus preserved.
          Это работает потому что после P1.3.4 lift state up DebtModal не имеет hooks
          внутри (useState/useRef все в parent). */}
      {showDebtModal && DebtModal()}

      {/* НОВОЕ: Добавляем вызов окна просмотра долга */}
      {viewDebt && <ViewDebtModal viewDebt={viewDebt} setViewDebt={setViewDebt} data={data} t={t} isCompact={isCompact} perms={perms} user={user} uOrg={uOrg} apiSync={apiSync} delDebt={delDebt} orgId={orgId} />}
      {showInvoiceImport && <InvoiceImportModal data={data} uOrg={uOrg} user={user} apiSync={apiSync} orgId={orgId} isCompact={isCompact} t={t} onClose={() => setShowInvoiceImport(false)} />}

      <PageHeader title={S().debts.title} sub={S().debts.subtitle} />

      {/* Кнопка импорта — над двумя колонками */}
      {perms.manageDebts && (
        <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
          <Btn variant="ghost" onClick={()=>setShowInvoiceImport(true)} style={{padding:'8px 16px',fontSize:13,border:`1px solid ${t.border}`}}>
            <Upload size={15}/> {S().debts.uploadInvoice}
          </Btn>
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:isCompact?'1fr':'1fr 1fr',gap:20}}>
        <Card style={{borderTop:`4px solid ${t.accentGreen}`}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
            <div>
              <div style={{display:'flex',alignItems:'center',gap:8,fontSize:16,fontWeight:800,color:t.accentGreen}}><ArrowRight size={20}/> {S().debts.receivableTitle}</div>
              <div style={{fontSize:13,color:t.textMuted,marginTop:4}}>{S().debts.receivableSubtitle}</div>
            </div>
            <div style={{textAlign:'right'}}>
              <div style={{fontSize:11,color:t.textMuted,textTransform:'uppercase',fontWeight:700}}>{S().debts.totalExpected}</div>
              <div className="mono" style={{fontSize:18,fontWeight:800,color:t.accentGreen}}>{fmt(totalReceivables)}</div>
            </div>
          </div>
          {perms.manageDebts && <Btn variant="success" style={{width:'100%',justifyContent:'center',marginBottom:14,padding:12}} onClick={()=>setShowDebtModal('receivable')}><Plus size={16}/> {S().debts.addDebtor}</Btn>}
          {/* Task #43: поиск + сортировка для receivables */}
          {renderDebtListControls(recSearch, setRecSearch, recSort, setRecSort, t.accentGreen)}
          <div>{receivables.length === 0 ? <div style={{textAlign:'center',padding:30,color:t.textMuted,fontSize:13}}>{recSearch ? 'Ничего не найдено по запросу' : S().debts.noActiveDebts}</div> : receivables.map(renderDebtLine)}</div>
        </Card>

        <Card style={{borderTop:`4px solid ${t.accentRed}`}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
            <div>
              <div style={{display:'flex',alignItems:'center',gap:8,fontSize:16,fontWeight:800,color:t.accentRed}}><ArrowLeft size={20}/> {S().debts.payableTitle}</div>
              <div style={{fontSize:13,color:t.textMuted,marginTop:4}}>{S().debts.payableSubtitle}</div>
            </div>
            <div style={{textAlign:'right'}}>
              <div style={{fontSize:11,color:t.textMuted,textTransform:'uppercase',fontWeight:700}}>{S().debts.totalOwed}</div>
              <div className="mono" style={{fontSize:18,fontWeight:800,color:t.accentRed}}>{fmt(totalPayables)}</div>
            </div>
          </div>
          {perms.manageDebts && <Btn variant="danger" style={{width:'100%',justifyContent:'center',marginBottom:14,padding:12}} onClick={()=>setShowDebtModal('payable')}><Plus size={16}/> {S().debts.addOurDebt}</Btn>}
          {/* Task #43: поиск + сортировка для payables */}
          {renderDebtListControls(paySearch, setPaySearch, paySort, setPaySort, t.accentRed)}
          <div>{payables.length === 0 ? <div style={{textAlign:'center',padding:30,color:t.textMuted,fontSize:13}}>{paySearch ? 'Ничего не найдено по запросу' : S().debts.noActiveDebts}</div> : payables.map(renderDebtLine)}</div>
        </Card>
      </div>
    </div>
  );
}
